import { Copy, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useAccount, useSwitchChain, useWriteContract } from 'wagmi';
import { baseSepolia, bscTestnet, sepolia } from 'wagmi/chains';
import { parseUnits } from 'viem';

type WalletWatchAssetRequest = {
  method: 'wallet_watchAsset';
  params: {
    type: 'ERC20';
    options: {
      address: `0x${string}`;
      symbol: string;
      decimals: number;
      image?: string;
    };
  };
};

type EthereumProvider = {
  request: <T = unknown>(args: WalletWatchAssetRequest) => Promise<T>;
};

type EthereumWindow = Window &
  typeof globalThis & {
    ethereum?: EthereumProvider;
  };

const getEthereumProvider = (): EthereumProvider | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const { ethereum } = window as EthereumWindow;
  return ethereum ?? null;
};

type FaucetToken = {
  name: string;
  symbol: string;
  contractAddress: `0x${string}`;
  claimAmountLabel: string;
  amount: string;
  decimals: number;
  chainId: number;
  icon?: string;
};

type FaucetNetwork = {
  label: string;
  chainId: number;
  tokens: FaucetToken[];
};

const faucetAbi = [
  {
    name: 'mint',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'to', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [],
  },
] as const;

const defaultNetworks: FaucetNetwork[] = [
  {
    label: 'BSC-testnet',
    chainId: bscTestnet.id,
    tokens: [
      {
        name: 'USDC-test',
        symbol: 'USDCt',
        contractAddress: '0xe88a2a1F904FeAA4B608aDeB3C20a5A759822061',
        claimAmountLabel: 'Claim 100,000',
        amount: '100000',
        decimals: 6,
        chainId: bscTestnet.id,
      },
      {
        name: 'USDT-test',
        symbol: 'USDTt',
        contractAddress: '0xede966f6298fe9103E6229F3895bAd758d7874Ed',
        claimAmountLabel: 'Claim 100,000',
        amount: '100000',
        decimals: 6,
        chainId: bscTestnet.id,
      },
    ],
  },
  {
    label: 'ETH-testnet',
    chainId: sepolia.id,
    tokens: [
      {
        name: 'USDC-test',
        symbol: 'USDCt',
        contractAddress: '0x9D20618957666aDf2C5d657b7B3be1FE30995C20',
        claimAmountLabel: 'Claim 100,000',
        amount: '100000',
        decimals: 6,
        chainId: sepolia.id,
      },
      {
        name: 'USDT-test',
        symbol: 'USDTt',
        contractAddress: '0x44ed554DeFC563db772A7B6D94D8D55F24b8aFA7',
        claimAmountLabel: 'Claim 100,000',
        amount: '100000',
        decimals: 6,
        chainId: sepolia.id,
      },
    ],
  },
  {
    label: 'BASE-testnet',
    chainId: baseSepolia.id,
    tokens: [
      {
        name: 'USDC-test',
        symbol: 'USDCt',
        contractAddress: '0xe88a2a1F904FeAA4B608aDeB3C20a5A759822061',
        claimAmountLabel: 'Claim 100,000',
        amount: '100000',
        decimals: 6,
        chainId: baseSepolia.id,
      },
      {
        name: 'USDT-test',
        symbol: 'USDTt',
        contractAddress: '0xB08e5083927eebB1208679CfD46Aa14516Cd7431',
        claimAmountLabel: 'Claim 100,000',
        amount: '100000',
        decimals: 6,
        chainId: baseSepolia.id,
      },
    ],
  },
];

type GetTestTokensProps = {
  networks?: FaucetNetwork[];
  onOpen?: () => void;
  onClose?: () => void;
};

export function GetTestTokens({ networks = defaultNetworks, onOpen, onClose }: GetTestTokensProps) {
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    onOpen?.();
  };

  const handleClose = () => {
    setOpen(false);
    onClose?.();
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className="inline-flex h-[32px] items-center justify-center rounded-[8px] bg-[#39e5f5] px-3 text-[12px] font-medium leading-[1.5] text-[#090909] transition-colors hover:bg-[#39e5f5]/90 focus:outline-none focus:ring-2 focus:ring-[#39e5f5] focus:ring-offset-2 focus:ring-offset-[#0c0c0c] font-(--font-jakarta)"
      >
        Get Test Tokens
      </button>

      {open ? (
        <FaucetModal networks={networks} onClose={handleClose} />
      ) : null}
    </>
  );
}

type FaucetModalProps = {
  networks: FaucetNetwork[];
  onClose: () => void;
};

const copyToClipboard = async (value: string) => {
  if (typeof navigator === 'undefined' || !navigator.clipboard) return;
  try {
    await navigator.clipboard.writeText(value);
  } catch {
    // swallow copy errors silently
  }
};

function FaucetModal({ networks, onClose }: FaucetModalProps) {
  const { address, isConnected, chainId: activeChainId } = useAccount();
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync } = useWriteContract();
  const [claimingKey, setClaimingKey] = useState<string | null>(null);
  const [addingKey, setAddingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const ensureChain = async (targetChainId: number) => {
    if (activeChainId === targetChainId) return;
    if (!switchChainAsync) {
      throw new Error('Please switch to the correct network in your wallet');
    }
    await switchChainAsync({ chainId: targetChainId });
  };

  const handleClaim = async (token: FaucetToken) => {
    const key = `${token.contractAddress}-claim`;
    setClaimingKey(key);
    setError(null);
    try {
      if (!isConnected || !address) {
        throw new Error('Please connect your wallet first');
      }
      await ensureChain(token.chainId);

      await writeContractAsync({
        abi: faucetAbi,
        address: token.contractAddress,
        functionName: 'mint',
        args: [address, parseUnits(token.amount, token.decimals)],
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Claim failed, please try again';
      setError(message);
      // eslint-disable-next-line no-console
      console.error('claim failed', err);
    } finally {
      setClaimingKey(null);
    }
  };

  const handleAddToWallet = async (token: FaucetToken) => {
    const key = `${token.contractAddress}-add`;
    setAddingKey(key);
    setError(null);
    try {
      await ensureChain(token.chainId);
      const ethereum = getEthereumProvider();
      if (!ethereum) {
        throw new Error('No wallet detected');
      }
      await ethereum.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: token.contractAddress,
            symbol: token.symbol,
            decimals: token.decimals,
            image: token.icon,
          },
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Add failed, please try again';
      setError(message);
      // eslint-disable-next-line no-console
      console.error('add to wallet failed', err);
    } finally {
      setAddingKey(null);
    }
  };

  const renderButtons = (token: FaucetToken) => {
    const claimKey = `${token.contractAddress}-claim`;
    const addKey = `${token.contractAddress}-add`;
    const isClaiming = claimingKey === claimKey;
    const isAdding = addingKey === addKey;

    return (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => handleClaim(token)}
          disabled={isClaiming || isAdding}
          className="flex h-[24px] items-center justify-center rounded-[8px] border border-[#39e5f5] px-2.5 text-[12px] font-medium leading-[1.5] text-[#39e5f5] transition-colors hover:bg-[#39e5f5]/10 focus:outline-none focus:ring-2 focus:ring-[#39e5f5] focus:ring-offset-2 focus:ring-offset-[#0c0c0c] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isClaiming ? 'Claiming...' : token.claimAmountLabel}
        </button>
        <button
          type="button"
          onClick={() => handleAddToWallet(token)}
          disabled={isClaiming || isAdding}
          className="flex h-[24px] items-center justify-center rounded-[8px] border border-[#39e5f5] px-2.5 text-[12px] font-medium leading-[1.5] text-[#39e5f5] transition-colors hover:bg-[#39e5f5]/10 focus:outline-none focus:ring-2 focus:ring-[#39e5f5] focus:ring-offset-2 focus:ring-offset-[#0c0c0c] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isAdding ? 'Adding...' : 'Add to Wallet'}
        </button>
      </div>
    );
  };

  const renderTokenRow = (token: FaucetToken, idx: number) => (
    <div
      key={`${token.contractAddress}-${idx}`}
      className="grid grid-cols-[120px_minmax(0,1fr)_auto] items-center gap-3 py-2 text-[12px] font-medium leading-[1.5] text-white font-(--font-jakarta)"
    >
      <span className="truncate">{token.name}</span>
      <div className="flex items-center gap-2">
        <span className="truncate text-white">
          Contract Address: {token.contractAddress}
        </span>
        <button
          type="button"
          aria-label="Copy contract address"
          onClick={() => copyToClipboard(token.contractAddress)}
          className="text-[#6b6b6b] transition-colors hover:text-white"
        >
          <Copy className="h-4 w-4" strokeWidth={2} />
        </button>
      </div>
      {renderButtons(token)}
    </div>
  );

  const bodyContent = useMemo(
    () =>
      networks.map((network, networkIndex) => (
        <div key={network.label}>
          <div className="mb-2 text-[14px] font-medium leading-[1.5] text-[#8c8c8c] font-(--font-jakarta)">
            {network.label}
          </div>
          <div className="space-y-1">
            {network.tokens.map((token, tokenIndex) => renderTokenRow(token, tokenIndex))}
          </div>
          {networkIndex < networks.length - 1 ? (
            <div className="mt-3 h-px w-full border-t border-[#2f2f2f]" />
          ) : null}
        </div>
      )),
    [networks, claimingKey, addingKey],
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 px-4 py-10 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-[740px] rounded-2xl border border-[#2f2f2f] bg-[#0c0f16] p-4 shadow-xl sm:p-5"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-[20px] font-normal leading-[1.5] text-white font-(--font-lexend)">
            Get Test Tokens
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-[#8c8c8c] transition-colors hover:text-white"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>

        <div className="space-y-4">{bodyContent}</div>
        {error ? (
          <div className="mt-3 text-[12px] leading-[1.5] text-[#ff6b6b] font-(--font-jakarta)">
            {error}
          </div>
        ) : null}
      </div>
    </div>
  );
}
